import { Component, Input } from '@angular/core';
import { PortalDeterminationService } from '@core/services/portal-determination.service';
import { Applicant } from '@core/typings/applicant.typing';
import { User } from '@core/typings/client-user.typing';
import { AddressFormatterService } from '@yourcause/common';
import { AnalyticsService, EventType } from '@yourcause/common/analytics';
import { FileService } from '@yourcause/common/files';
import { I18nService } from '@yourcause/common/i18n';
import { YCModalComponent } from '@yourcause/common/modals';
import * as parse from 'papaparse';

@Component({
  selector: 'yc-personal-data-modal',
  templateUrl: './personal-data-modal.component.html',
  styleUrls: ['./personal-data-modal.component.scss']
})
export class PersonalDataModalComponent extends YCModalComponent<void> {
  @Input() user: Applicant|User;

  firstNameText = this.i18n.translate('common:lblFirstName', {}, 'First name');
  lastNameText = this.i18n.translate('common:lblLastName', {}, 'Last name');
  emailText = this.i18n.translate('common:textEmail', {}, 'Email');
  phoneText = this.i18n.translate('common:textPhoneNumber', {}, 'Phone number');
  addressText = this.i18n.translate('common:textAddress', {}, 'Address');
  jobTitleText = this.i18n.translate('common:textJobTitle', {}, 'Job title');
  downloadAccountDataHelpLinkApplicant = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-applicants-download-data.html';
  downloadAccountDataHelpLinkManager = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-account-download-data.html';

  constructor (
    private fileService: FileService,
    private determinationService: PortalDeterminationService,
    private i18n: I18nService,
    private addressFormatter: AddressFormatterService,
    private analyticsService: AnalyticsService
  ) {
    super();
  }

  get jobTitle () {
    return 'jobTitle' in this.user ? this.user.jobTitle : '';
  }

  get phoneNumber () {
    return 'phoneNumber' in this.user ? this.user.phoneNumber : '';
  }

  get isManager () {
    return this.determinationService.isManager;
  }

  get simpleAddress () {
    return {
      address: (this.user as Applicant).address,
      address2: (this.user as Applicant).address2,
      city: (this.user as Applicant).city,
      state: (this.user as Applicant).state,
      country: (this.user as Applicant).country,
      postalCode: (this.user as Applicant).postalCode
    };
  }

  get usersAddress () {
    return this.addressFormatter.format({
      address1: (this.user as Applicant).address,
      address2: (this.user as Applicant).address2,
      city: (this.user as Applicant).city,
      stateProvRegCode: (this.user as Applicant).state,
      country: (this.user as Applicant).country,
      postalCode: (this.user as Applicant).postalCode
    }, true);
  }

  handlePrimaryClick () {
    this.analyticsService.emitEvent({
      eventName: 'Download personal data',
      eventType: EventType.Click,
      extras: null
    });

    return this.isManager ? this.downloadForManager() : this.downloadForPortal();
  }

  downloadForPortal () {
    const applicant = [{
      [this.firstNameText]: this.user.firstName,
      [this.lastNameText]: this.user.lastName,
      [this.emailText]: this.user.email,
      [this.phoneText]: this.phoneNumber,
      [this.addressText]: this.addressFormatter.formatSimpleGrantsAddressToSingleLine(this.simpleAddress)
    }];
    const csv = parse.unparse(applicant);
    this.fileService.downloadCSV(csv);
  }

  downloadForManager () {
    const manager = [{
      [this.firstNameText]: this.user.firstName,
      [this.lastNameText]: this.user.lastName,
      [this.emailText]: this.user.email,
      [this.jobTitleText]: this.jobTitle
    }];
    const csv = parse.unparse(manager);
    this.fileService.downloadCSV(csv);
  }

  onCancel () {
    this.closeModal.emit();
  }
}
